import type { FilterState, SortKey, SortState } from '@mona-roadmap/domain';
import { ArrowDown, ArrowUp, Filter } from 'lucide-react';
import { sortKeyOf, type ColumnDef, type ColumnKey } from '../lib/columns';
import { isColumnActive } from './FilterEditor';

interface Props {
  columns: ColumnDef[];
  filters: FilterState;
  filtersActive: boolean;
  sort: SortState;
  /** Click on a header: sort by that column (again: descending, then back to the default). */
  onSort: (key: SortKey) => void;
  /** Opens the shared filter editor on this column, hanging from the clicked funnel. */
  onOpenFilter: (column: ColumnKey, anchor: HTMLElement) => void;
}

export function GridHeader({ columns, filters, filtersActive, sort, onSort, onOpenFilter }: Props) {
  return (
    <div role="row" className="tg-row tg-head" aria-label="Column headers">
      {columns.map((c) => {
        const active = isColumnActive(c, filters);
        const sortKey = sortKeyOf(c);
        const sorted = sortKey !== null && sort.key === sortKey;
        return (
          <div key={c.key} role="columnheader" className="tg-cell" data-col={c.key} title={c.hint} aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            {sortKey ? (
              <button type="button" className="th-sort" data-sorted={sorted || undefined} aria-label={`Sort by ${c.label}`} title={`Sort by ${c.label}`} onClick={() => onSort(sortKey)}>
                <span className="truncate">{c.label}</span>
                {sorted ? (sort.dir === 'asc' ? <ArrowUp className="size-3.5 flex-none" aria-hidden /> : <ArrowDown className="size-3.5 flex-none" aria-hidden />) : <ArrowUp className="sort-hint size-3.5 flex-none" aria-hidden />}
              </button>
            ) : (
              <span className="truncate">{c.label}</span>
            )}
            {c.filter ? (
              <button
                type="button"
                className={`grid size-6 flex-none place-items-center rounded hover:bg-hover ${active ? 'text-accent-ink' : 'text-faint'}`}
                aria-label={`Filter ${c.label}${active ? ' (active)' : ''}`}
                data-active={active || undefined}
                onClick={(e) => onOpenFilter(c.key, e.currentTarget)}
              >
                <Filter className="size-3.5" fill={active ? 'currentColor' : 'none'} aria-hidden />
              </button>
            ) : null}
            {c.key === 'progress' && filtersActive ? (
              <span className="rounded bg-accent-soft px-1.5 text-[11.5px] font-medium normal-case tracking-normal text-accent-ink" title="Counts reflect active filters">
                filtered
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
